import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type { SlashItem } from './slashItems'

interface SlashCommandListProps {
  items: SlashItem[]
  command: (item: SlashItem) => void
}

export interface SlashCommandListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

// Keyboard-navigable popup driven by @tiptap/suggestion. The suggestion plugin
// calls onKeyDown imperatively for arrow/enter while the user keeps typing.
export const SlashCommandList = forwardRef<SlashCommandListRef, SlashCommandListProps>(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => setSelected(0), [ items ])

  useEffect(() => {
    containerRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [ selected ])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelected((selected + items.length - 1) % items.length)
        return true
      }
      if (event.key === 'ArrowDown') {
        setSelected((selected + 1) % items.length)
        return true
      }
      if (event.key === 'Enter') {
        const item = items[selected]
        if (item) command(item)
        return true
      }
      return false
    },
  }), [ selected, items, command ])

  if (items.length === 0) {
    return (
      <div className="w-64 rounded-md border border-border bg-popover p-2 text-sm text-muted-foreground shadow-md">
        No matches
      </div>
    )
  }

  return (
    <div ref={containerRef} className="max-h-72 w-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
      {items.map((item, index) => {
        const Icon = item.icon
        return (
          <button
            key={item.title}
            type="button"
            data-active={index === selected}
            onMouseEnter={() => setSelected(index)}
            onClick={() => command(item)}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-left',
              index === selected && 'bg-muted',
            )}
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded border border-border bg-background">
              <Icon className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground">{item.title}</span>
              <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
})

SlashCommandList.displayName = 'SlashCommandList'
